import React, { useCallback, useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import ActionButton, { ActionButtonVariant } from '@/pages/table/components/ActionButton/ActionButton';
import BetSliderLegend from '@/pages/table/components/BetSliderLegend/BetSliderLegend';
import useRaiseButtons from '@/pages/table/hooks/useRaiseButtons';
import useIsMobileView from '@/hooks/useIsMobileView';
import { IoClose } from 'react-icons/io5';
import { useDispatch } from 'react-redux';

import styles from './BetSlider.module.css';
import { betAction, raiseAction } from '@/services/table/tableActions';
import useChatInputFocus from '@/pages/table/hooks/useChatInputFocus';

interface BetSliderProps {
  min: number | undefined;
  max: number | undefined;
  step: number | undefined;
  onChange: (selectedValue: number) => void;
  value: number | undefined;
  disabled?: boolean;
  bigBlindValue: number | undefined;
  handPhase: HandPhases.ON_BET | HandPhases.POST_FLOP | HandPhases.PRE_FLOP;
  pot: number | undefined;
  currentBet: number | undefined;
  showAllIn: boolean;
  isPlayerTurn: boolean;
  setShowBetSlider?: ((value: boolean) => void) | undefined;
  tableId?: string;
  canBet?: boolean;
  canRaise?: boolean;
  shouldDisplayInBB?: boolean;
  containerClassName?: string;
}

export enum HandPhases {
  PRE_FLOP = 'preFlop',
  POST_FLOP = 'postFlop',
  ON_BET = 'onBet',
}

const formatBet = (bet: number): number => {
  return bet % 1 === 0 ? bet : parseFloat(bet.toFixed(2));
};

const formatDisplayValue = (value: number, displayInBB: boolean, bbValue: number) => {
  const formattedValue = displayInBB ? (value / bbValue).toFixed(2) : value.toFixed(2);
  return displayInBB ? `${formattedValue} BB` : formattedValue;
};

const parseInputValue = (input: string, displayInBB: boolean, bbValue: number) => {
  const cleanedValue = input.replace(/ BB$/, ''); // Elimina el sufijo "BB" si está presente
  const numValue = parseFloat(cleanedValue);
  return displayInBB ? numValue * bbValue : numValue;
};

const BetSlider = React.forwardRef<HTMLDivElement, BetSliderProps>(
  (
    {
      min = 0,
      max = 0,
      onChange,
      disabled = false,
      showAllIn,
      value = 0,
      currentBet = 0,
      pot = 0,
      bigBlindValue = 0,
      handPhase,
      setShowBetSlider,
      isPlayerTurn,
      step = 0,
      tableId,
      canBet,
      canRaise,
      shouldDisplayInBB,
      containerClassName = '',
    },
    ref,
  ) => {
    const dispatch = useDispatch();
    const [maintainFocus, setMaintainFocus] = useState(false);
    const { isChatInputFocused } = useChatInputFocus();

    const [sliderValue, setSliderValue] = useState(formatBet(value || min));
    const [rawInputValue, setRawInputValue] = useState(
      formatDisplayValue(value || min, !!shouldDisplayInBB, bigBlindValue),
    );

    const refOnchange = useRef(false);
    const lastValidValue = useRef(sliderValue);

    const inputRef = useRef<HTMLInputElement | null>(null);

    const adjustedStep = 0.01;

    const isMobileDevice = useIsMobileView({ checkForUserAgent: true });

    const getNewValue = useCallback((value: number) => formatBet(Math.max(min, Math.min(max, value))), [max, min]);

    const focusInput = useCallback(() => {
      if (inputRef.current && !isMobileDevice && !isChatInputFocused) {
        inputRef.current.focus();
        inputRef.current.select();
      }
    }, [isMobileDevice, isChatInputFocused]);

    useEffect(() => {
      focusInput();
    }, [focusInput]);

    useEffect(() => {
      setSliderValue(value);
      focusInput();
    }, [value, focusInput]);

    const propagateNewValue = useCallback(
      (newValue: number) => {
        const formattedValue = getNewValue(newValue);
        setSliderValue(formattedValue);
        setRawInputValue(formatDisplayValue(formattedValue, !!shouldDisplayInBB, bigBlindValue));
        onChange(formattedValue);
        if (!isMobileDevice) focusInput();
      },
      [getNewValue, onChange, focusInput, shouldDisplayInBB, bigBlindValue],
    );

    useEffect(() => {
      const newValue = value !== undefined ? value : min;
      propagateNewValue(newValue);
    }, [value, min, propagateNewValue]);

    useEffect(() => {
      const handleWheel = (event: WheelEvent) => {
        if (!disabled) {
          event.preventDefault();
          const direction = event.deltaY < 0 ? 1 : -1;
          const newValue = sliderValue + direction * step;
          propagateNewValue(newValue);
        }
      };

      window.addEventListener('wheel', handleWheel);

      return () => {
        window.removeEventListener('wheel', handleWheel);
      };
    }, [sliderValue, step, disabled, propagateNewValue]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      if (/^-?\d*\.?\d{0,2}( BB)?$/.test(newValue)) {
        setRawInputValue(newValue);
      }
    };

    const handleBlur = () => {
      setMaintainFocus(false);
      const numValue = parseInputValue(rawInputValue, !!shouldDisplayInBB, bigBlindValue);
      if (!isNaN(numValue)) {
        const formattedValue = getNewValue(numValue);
        setRawInputValue(formatDisplayValue(formattedValue, !!shouldDisplayInBB, bigBlindValue));
        setSliderValue(formattedValue);
        onChange(formattedValue);
      } else {
        setRawInputValue(formatDisplayValue(sliderValue, !!shouldDisplayInBB, bigBlindValue));
      }
    };

    const handleEnterPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        handleBlur();

        const { currentTarget } = event;

        if (!tableId) return;

        if (canBet) return dispatch(betAction(tableId, Number(currentTarget.value)));

        if (canRaise) return dispatch(raiseAction(tableId, Number(currentTarget.value)));
      }
    };

    const { buttonsToShow } = useRaiseButtons({
      max,
      currentBet,
      pot,
      bigBlindValue,
      handPhase,
      showAllIn,
    });

    const potIsMax = buttonsToShow.find((button) => button.label === 'Pot')?.bet === max;

    const onChangeRangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (maintainFocus) {
        setMaintainFocus(false);
      } else {
        const newValue = parseFloat(e.target.value);
        const difference = newValue - lastValidValue.current;
        if (refOnchange.current) {
          if (Math.abs(difference) > 0) {
            if (difference > 0) {
              propagateNewValue(sliderValue + bigBlindValue);
              lastValidValue.current = sliderValue + bigBlindValue;
            } else {
              propagateNewValue(sliderValue - bigBlindValue);
              lastValidValue.current = sliderValue - bigBlindValue;
            }
            refOnchange.current = false;
          } else {
            // Handle the initial click without moving the slider
            refOnchange.current = false;
          }
        } else {
          if (newValue <= min + adjustedStep) {
            propagateNewValue(min);
            lastValidValue.current = min;
          } else if (newValue >= max - adjustedStep) {
            propagateNewValue(max);
            lastValidValue.current = max;
          } else {
            propagateNewValue(newValue);
            lastValidValue.current = newValue;
          }
        }
      }
    };

    if (isMobileDevice) {
      return (
        <div className={containerClassName} ref={ref}>
          <div className="absolute -top-2.5 left-[8.75em] z-50 h-8 w-8 rounded-full bg-phenom-border-glow p-[2px] hover:cursor-pointer">
            <div
              onClick={() => setShowBetSlider?.(false)}
              className="flex h-full w-full items-center justify-center rounded-full bg-pn-violet-medium align-middle hover:bg-ph-violet-dark"
            >
              <IoClose className="text-lg text-ph-gray-silver" />
            </div>
          </div>
          <div className="bg-phenom-background z-40 rounded-[27px] p-[1px] border-glow">
            <div className={twMerge('h-[25em] w-40 rounded-[27px]', styles.container)}>
              <div className="h-[14.25em]">
                <div className="relative h-[14.25em]">
                  <div className="slider-track absolute z-[-1] h-[14.25em] w-40" />
                  <div
                    className="absolute left-20 w-1.5 rounded-full bg-ph-orange-secondary"
                    style={{
                      height: `${((sliderValue - min) / (max - min)) * 100}%`,
                      marginTop: '0.0625em',
                      bottom: '-3.5em',
                    }}
                  />

                  <div className="absolute -left-[1.6875em] top-[10.125em] flex">
                    <input
                      type="range"
                      disabled={disabled}
                      min={min}
                      max={max}
                      step={adjustedStep}
                      value={sliderValue}
                      onMouseDown={() => {
                        refOnchange.current = true;
                      }}
                      onTouchStart={() => {
                        refOnchange.current = true;
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        refOnchange.current = true;
                      }}
                      onChange={onChangeRangeHandler}
                      className={twMerge(
                        'h-1.5 w-[14.25em] origin-bottom rotate-[-90deg] appearance-none rounded-full bg-white bg-opacity-10',
                        styles.customSlider,
                      )}
                    />
                    <div className="absolute -top-[6.25em] left-[8.875em]">
                      <BetSliderLegend
                        shouldDisplayInBB={!!shouldDisplayInBB}
                        bigBlindValue={bigBlindValue}
                        max={max}
                        min={min}
                      />
                    </div>
                  </div>
                  {(showAllIn || potIsMax) && (
                    <div className="bg-black-100 absolute left-[50%] top-[1.125em] -translate-x-[50%] md:left-[2.1875em]">
                      <ActionButton
                        variant={ActionButtonVariant.AllIn}
                        isChecked={false}
                        isPlayerTurn={isPlayerTurn}
                        onClick={() => propagateNewValue(max)}
                      >
                        <span className="text-xxs">{potIsMax ? 'Pot' : 'ALL IN'}</span>
                      </ActionButton>
                    </div>
                  )}
                </div>
              </div>

              <input
                ref={inputRef}
                className={twMerge(styles.input, 'text-[16px]')}
                value={rawInputValue}
                onChange={handleInputChange}
                onBlur={handleBlur}
                onKeyDown={handleEnterPress}
                type="number"
                pattern="[0-9]*[.]?[0-9]*"
                step="0.01"
                onFocus={(e) => {
                  e.target.select();
                  setMaintainFocus(true);
                }}
                onTouchStart={(e) => {
                  if (e.target === inputRef.current) {
                    e.stopPropagation();
                  }
                }}
              />
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className={twMerge('flex w-full justify-between gap-4', !isPlayerTurn ? 'invisible' : '')}>
        <div className="relative">
          <div className="absolute bottom-0">
            <div
              className="absolute left-0 top-[0.75em] z-[1] h-1.5 rounded-full bg-ph-purple"
              style={{
                width: `${((sliderValue - min) / (max - min)) * 100}%`,
                marginLeft: '0.0625em',
              }}
            />
            <input
              type="range"
              disabled={disabled}
              min={min}
              max={max}
              step={adjustedStep}
              value={sliderValue}
              onClick={(e) => {
                refOnchange.current = true;
                e.preventDefault();
              }}
              onChange={onChangeRangeHandler}
              className={twMerge(
                'relative z-[5] h-1.5 w-[41em] origin-bottom appearance-none rounded-full border-transparent bg-white bg-opacity-10 !outline-none focus:border-transparent focus:ring-0 md:w-[23em]',
                styles.customSlider,
                styles.desktopSlider,
              )}
            />
          </div>
        </div>
        <input
          ref={inputRef}
          type="text"
          value={rawInputValue}
          onChange={handleInputChange}
          onKeyDown={handleEnterPress}
          onBlur={handleBlur}
          className={styles.desktopInput}
        />
      </div>
    );
  },
);

export default BetSlider;
